/**
 * <kc-parallax> — scroll parallax for the newsletter banner (sections/kc-newsletter.liquid).
 *
 * The element wraps the section; its `.kc-newsletter__bg` child is the photo
 * layer. While the section is near the viewport we move that layer with a
 * transform so it scrolls slower than the page. Travel is a share of the
 * section height (data-speed, 0.25 = 25%), which keeps the required bleed
 * small so wide, short photos are not zoomed too far.
 *
 * - Transform only (compositor), scroll listener is passive + rAF-throttled
 * - IntersectionObserver starts/stops the listener when off-screen
 * - Honors prefers-reduced-motion and the data-mobile="false" toggle
 * - Custom element, so the theme editor re-init is automatic
 */
class KcParallax extends HTMLElement {
  connectedCallback() {
    this.bg = this.querySelector('.kc-newsletter__bg');
    if (!this.bg || this.hasAttribute('data-disabled')) return;

    this.speed = Math.max(0, parseFloat(this.dataset.speed || '0.25')) || 0.25;
    this.allowMobile = this.dataset.mobile !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.ticking = false;
    this.active = false;
    this.bleed = 0;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => {
      this.measure();
      this.update();
    };

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop())),
      { rootMargin: '25% 0px' }
    );
    this.observer.observe(this);

    this.reducedMotion.addEventListener('change', this.onResize);
    this.mobileQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.measure();
    this.update();
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onResize);
    if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
  }

  get enabled() {
    if (this.reducedMotion.matches) return false;
    if (!this.allowMobile && this.mobileQuery.matches) return false;
    return true;
  }

  /** Bleed = half the total travel, so the layer can move up or down that far. */
  measure() {
    if (!this.enabled) {
      this.bleed = 0;
      this.style.removeProperty('--kc-bleed');
      return;
    }
    this.bleed = Math.ceil((this.offsetHeight * this.speed) / 2);
    this.style.setProperty('--kc-bleed', `${this.bleed}px`);
  }

  update() {
    if (!this.enabled) {
      this.bg.style.transform = '';
      return;
    }
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    // -1 when the section has just entered from the bottom, +1 when it is
    // about to leave at the top, 0 when centered in the viewport.
    const range = (viewport + rect.height) / 2;
    const progress = Math.max(-1, Math.min(1, (viewport / 2 - (rect.top + rect.height / 2)) / range));
    const shift = progress * this.bleed;
    this.bg.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
  }

  start() {
    if (this.active) return;
    this.active = true;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.measure();
    this.update();
  }

  stop() {
    if (!this.active) return;
    this.active = false;
    window.removeEventListener('scroll', this.onScroll);
  }
}

if (!customElements.get('kc-parallax')) {
  customElements.define('kc-parallax', KcParallax);
}
